/*
 * Runtime config for the AquaStar admin console.
 *
 * The appId is the single value that ties this SPA to the same
 * Gateway-side App-Definitions bundle and per-app DB used by the
 * iOS app and (eventually) the AquaStar user web app. Do not
 * hard-code the string elsewhere — read it from here so we never
 * ship a build with a mismatched id.
 *
 *   iOS sends:   { "appId": "aquastar" }     (Network+Auth.swift)
 *   App-Defs:    App-Definitions/aquastar/1.0.0/manifest.json
 *   Per-app DB:  app_aquastar_<stage>
 */

export const APP_ID = 'aquastar' as const;

/**
 * Gateway base URL.
 *
 * Production builds: empty string. The deployed SPA sits behind Caddy,
 * which reverse-proxies `/auth/*` and `/v1/*` to the Gateway on the
 * same origin (see Web/Admin/deployments/aquastar-admin-{stage}.caddy).
 * Using an empty base means every `fetch()` is same-origin → no
 * preflight, no CORS, no `Access-Control-Allow-Origin` headers needed
 * on the Gateway. Same trick Vault Web uses.
 *
 * Local dev (`npm run dev` against vite's localhost server): set
 * VITE_GATEWAY_BASE in a `.env.development.local` to e.g.
 * `https://gateway-dev.kstack.app`, so the dev server can reach a
 * remote Gateway directly (CORS may still bite — for that, set up
 * a Vite proxy in vite.config.ts under `server.proxy`).
 */
export const GATEWAY_BASE: string =
  (import.meta.env.VITE_GATEWAY_BASE as string | undefined)?.replace(/\/+$/, '') ??
  '';

/**
 * Build the canonical app-scoped function URL. Every admin handler
 * lives under /v1/app/<appId>/fn/<name>, gated server-side by the
 * per-app aquastar_staff role table — see PLAN_WEB_ADMIN_APP.md §6.
 */
export function appFnUrl(name: string): string {
  return `${GATEWAY_BASE}/v1/app/${APP_ID}/fn/${name}`;
}

/**
 * Build the SSE stream URL. Streams are declared in the AquaStar
 * manifest's `streams[]` and broadcast by the device translator.
 */
export function appStreamUrl(name: string): string {
  return `${GATEWAY_BASE}/v1/app/${APP_ID}/stream/${name}`;
}

/**
 * Stage label, surfaced in the topbar so it's obvious which
 * gateway the operator is pointed at. Mirrors the deploy-time
 * VITE_STAGE.
 */
export const STAGE: 'dev' | 'uat' | 'prod' =
  ((import.meta.env.VITE_STAGE as string | undefined) ?? 'dev') as
    | 'dev'
    | 'uat'
    | 'prod';

/**
 * Where a build lands once CI has run, and who to ask for access to it.
 *
 * AQUASTAR-423/-357. The Firmware page tells an operator what happens after a
 * release is recorded, and "it goes to TestFlight / Firebase" is useless to
 * anyone who has never been added to either. Two audiences, so two kinds of
 * link: consoles for whoever can actually grant access, and a prefilled
 * request for whoever just needs to be added.
 *
 * TestFlight distributes the iOS build; Firebase App Distribution does the
 * same job for Android. They are unrelated services — an Android tester will
 * never appear in TestFlight, and an iOS build will never show up in Firebase,
 * which is a question this page gets asked more than once.
 */
export const DISTRIBUTION = {
  ios: {
    name: 'TestFlight',
    what: "Apple's tester distribution for iOS. A build that finishes in Xcode Cloud is uploaded here, and testers on the list install it through the TestFlight app.",
    console: 'https://appstoreconnect.apple.com/apps',
    consoleLabel: 'App Store Connect → TestFlight',
    ci: 'https://appstoreconnect.apple.com/teams',
    ciLabel: 'Xcode Cloud builds',
  },
  android: {
    name: 'Firebase App Distribution',
    what: 'Google\u2019s tester distribution for Android. The dev and UAT builds are uploaded here by GitHub Actions; testers get an email invitation and install the APK directly. Production goes to a Play Store track instead.',
    console: 'https://console.firebase.google.com/project/_/appdistribution',
    consoleLabel: 'Firebase App Distribution',
    ci: 'https://github.com/ekiss71/nexus-aquastar-android/actions',
    ciLabel: 'GitHub Actions runs',
  },
} as const;

/**
 * Who adds a tester. Left as one constant so it is changed in one place when
 * it stops being Erwin.
 */
export const TESTER_ACCESS_CONTACT = 'ekiss71@gmail.com';
